import { last } from 'lodash';

import type {
  AvailableTransportIngredientResponse,
  DurationDoubleRange,
  FerryIngredientAdapter,
  FerryIngredientResponse,
  FlightIngredientAdapter,
  FlightIngredientResponse,
  NonNullableProperties,
  TransportAlternative,
  TransportIngredientAdapter,
  UnavailableTransportIngredientResponse,
} from '@libs/trip-planner-interfaces';
import { IngredientType, TransportType } from '@libs/trip-planner-interfaces';
import { assertValue, isDefined, normalizeDate } from '@libs/utils';
import { SemboDate } from '@libs/sembo-dates';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

import { toLeg } from './legResponseQL';
import { toMoney } from './moneyResponseQL';
import type { IngredientResponseQL } from './itineraryResponseQL';

export type TransportIngredientResponseQL = Extract<
  IngredientResponseQL,
  { type: 'TransportIngredientResponseQL' }
>;
export function toTransportIngredientResponse(
  transportIngredientResponseQL: TransportIngredientResponseQL,
  transportIngredientAdapter: TransportIngredientAdapter
): FlightIngredientResponse | FerryIngredientResponse {
  let isFlight: boolean;
  if (transportIngredientResponseQL.legs) {
    assertValue(
      transportIngredientResponseQL.legs,
      'search-service-smart-search > itinerarySearchService.ts > class ItinerarySearchService > legs'
    );
    const firstLeg = transportIngredientResponseQL.legs[0];
    assertValue(
      firstLeg,
      'search-service-smart-search > itinerarySearchService.ts > class ItinerarySearchService > firstLeg'
    );
    const firstSegment = toLeg(firstLeg).segments[0];
    assertValue(
      firstSegment,
      'search-service-smart-search > itinerarySearchService.ts > class ItinerarySearchService > firstSegment'
    );
    isFlight = firstSegment.transportType === TransportType.Flight;
  } else {
    isFlight =
      transportIngredientAdapter.ingredientType === IngredientType.Flight;
  }

  if (isFlight) {
    const flightIngredient =
      transportIngredientAdapter as FlightIngredientAdapter;

    return toFlightIngredientResponse(
      transportIngredientResponseQL,
      flightIngredient
    );
  }

  // INFO: this case must return value. If new transport type will be added refactor to swich statement
  else {
    const ferryIngredient =
      transportIngredientAdapter as FerryIngredientAdapter;

    return toFerryIngredientResponse(
      transportIngredientResponseQL,
      ferryIngredient
    );
  }
}

export function toFlightIngredientResponse(
  result: TransportIngredientResponseQL,
  ingredientAdapter: FlightIngredientAdapter
): FlightIngredientResponse {
  if (isAvailableTransportIngredientResponseQL(result)) {
    const availableTransportIngredientResponse =
      toAvailableTransportIngredientResponse(result, ingredientAdapter);

    assertSmartSearchValue(result.legIndex);

    const leg = availableTransportIngredientResponse.legs[result.legIndex];

    assertValue(
      leg,
      'search-service-smart-search > mappers.ts > toFlightIngredientResponse > leg'
    );

    return {
      numberOfStops: leg.segments.length - 1,
      ...availableTransportIngredientResponse,
    };
  }

  return {
    legs: null,
    numberOfStops: 0,
    ...toUnavailableTransportIngredientResponse(result, ingredientAdapter),
  };
}

export function toFerryIngredientResponse(
  result: TransportIngredientResponseQL,
  ingredientAdapter: FerryIngredientAdapter
): FerryIngredientResponse {
  if (isAvailableTransportIngredientResponseQL(result)) {
    const availableTransportIngredientResponse =
      toAvailableTransportIngredientResponse(result, ingredientAdapter);

    assertSmartSearchValue(result.legIndex);
    const leg = availableTransportIngredientResponse.legs[result.legIndex];

    assertValue(
      leg,
      'search-service-smart-search > mappers.ts > toFerryIngredientResponse > leg'
    );

    const firstSegment = leg.segments[0];

    assertValue(
      firstSegment,
      'search-service-smart-search > mappers.ts > toFerryIngredientResponse > firstSegment'
    );

    const vehicles = firstSegment.vehicles;

    if (!vehicles) {
      throw new Error('FerryIngredientResponse should always have vehicles');
    }

    const transportationName = firstSegment.transportationName;
    const isCabinMandatory = firstSegment.isCabinMandatory;

    return {
      vehicles,
      transportationName,
      isCabinMandatory,
      ...availableTransportIngredientResponse,
    };
  }

  return {
    vehicles: null,
    transportationName: null,
    isCabinMandatory: null,
    ...toUnavailableTransportIngredientResponse(result, ingredientAdapter),
    alternativeSuggestion: null,
  };
}

type AvailableTransportIngredientResponseQL = NonNullableProperties<
  TransportIngredientResponseQL,
  'addToBasketToken' | 'allCarriers' | 'legs' | 'totalPrice'
>;

function isAvailableTransportIngredientResponseQL(
  transportIngredientResponse: TransportIngredientResponseQL
): transportIngredientResponse is AvailableTransportIngredientResponseQL {
  return (
    isDefined(transportIngredientResponse.addToBasketToken) &&
    isDefined(transportIngredientResponse.allCarriers) &&
    isDefined(transportIngredientResponse.legs) &&
    isDefined(transportIngredientResponse.totalPrice)
  );
}

function toAvailableTransportIngredientResponse(
  result: AvailableTransportIngredientResponseQL,
  ingredient: FlightIngredientAdapter | FerryIngredientAdapter
): AvailableTransportIngredientResponse {
  assertSmartSearchValue(result.legIndex);
  const legFragment = result.legs[result.legIndex];
  assertValue(
    legFragment,
    'search-service-smart-search > mappers.ts > toAvailableTransportIngredientResponse > legFragment'
  );

  const leg = toLeg(legFragment);

  const firstSegment = leg.segments[0];
  const lastSegment = last(leg.segments);

  if (!firstSegment || !lastSegment) {
    throw new Error('Leg segment not found');
  }

  const transportIds: string[] = [];
  const carriers: string[] = [];

  leg.segments.forEach((segment) => {
    transportIds.push(segment.transportId);
    carriers.push(segment.carrier);
  });

  const date = normalizeDate(
    new SemboDate(firstSegment.departureDateTime).startOf('day').get()
  );

  assertSmartSearchList(result.legs);
  const legs = result.legs.map(toLeg);

  const allCarriers = result.allCarriers.map((carrier) => {
    assertSmartSearchValue(carrier);
    return carrier;
  });

  const totalPrice = toMoney(result.totalPrice);

  return {
    ingredientId: ingredient.ingredientId,
    ingredientType: ingredient.ingredientType,
    addToBasketToken: result.addToBasketToken,
    transportIds,
    carriers,
    allCarriers,
    date,
    departureDate: new Date(firstSegment.departureDateTime),
    departureIata: firstSegment.departureIata,
    arrivalDate: new Date(lastSegment.arrivalDateTime),
    arrivalIata: lastSegment.arrivalIata,
    totalPrice,
    durationInMinutes: leg.durationInMinutes,
    durationRangeInMinutes: toDurationDoubleRange(
      result.durationRangeInMinutes
    ),
    legs,
    legIndex: result.legIndex,
    isAvailable: true,
    alternativeSuggestion: null,
    platingCarrier: leg.platingCarrier,
  };
}

type DurationDoubleRangeQL =
  TransportIngredientResponseQL['durationRangeInMinutes'];
function toDurationDoubleRange(
  durationDoubleRangeQL: DurationDoubleRangeQL
): DurationDoubleRange | undefined {
  if (!durationDoubleRangeQL) {
    return undefined;
  }

  assertSmartSearchValue(durationDoubleRangeQL.from);
  assertSmartSearchValue(durationDoubleRangeQL.to);

  return {
    from: durationDoubleRangeQL.from,
    to: durationDoubleRangeQL.to,
  };
}

function toUnavailableTransportIngredientResponse(
  result: TransportIngredientResponseQL,
  ingredient: FlightIngredientAdapter | FerryIngredientAdapter
): UnavailableTransportIngredientResponse {
  const alternativeSuggestion = result.alternativeSuggestion
    ? toAlternativeTransportSuggestion(result.alternativeSuggestion)
    : null;

  assertSmartSearchValue(result.date);

  if (!alternativeSuggestion) {
    return {
      ingredientId: ingredient.ingredientId,
      ingredientType: ingredient.ingredientType,
      date: new Date(result.date),
      totalPrice: null,
      isAvailable: false,
      addToBasketToken: null,
      alternativeSuggestion: null,
    };
  }

  assertSmartSearchValue(result.legIndex);
  const leg = alternativeSuggestion.legs[result.legIndex];
  assertValue(
    leg,
    'search-service-smart-search > mappers.ts > toAvailableTransportIngredientResponse > leg'
  );

  const firstSegment = leg.segments[0];

  if (!firstSegment) {
    throw new Error('Leg segment not found');
  }

  const date = normalizeDate(
    new SemboDate(firstSegment.departureDateTime).startOf('day').get()
  );

  return {
    ingredientId: ingredient.ingredientId,
    ingredientType: ingredient.ingredientType,
    addToBasketToken: null,
    date,
    totalPrice: null,
    isAvailable: false,
    alternativeSuggestion,
  };
}

type AlternativeSuggestionQL = NonNullable<
  TransportIngredientResponseQL['alternativeSuggestion']
>;
function toAlternativeTransportSuggestion(
  alternativeSuggestionQL: AlternativeSuggestionQL
): TransportAlternative {
  assertSmartSearchList(alternativeSuggestionQL.legs);

  const legs = alternativeSuggestionQL.legs.map(toLeg);

  const leg = legs[alternativeSuggestionQL.legIndex];

  assertValue(
    leg,
    'search-service-smart-search > mappers.ts > toAlternativeTransportSuggestion > leg'
  );

  const firstSegment = leg.segments[0];
  const lastSegment = last(leg.segments);

  if (!firstSegment || !lastSegment) {
    throw new Error('Alternative leg segment not found');
  }

  const transportIds: string[] = [];
  const carriers: string[] = [];

  leg.segments.forEach((segment) => {
    transportIds.push(segment.transportId);
    carriers.push(segment.carrier);
  });

  assertSmartSearchValue(alternativeSuggestionQL.totalPrice);

  return {
    addToBasketToken: alternativeSuggestionQL.addToBasketToken,
    legs,
    legIndex: alternativeSuggestionQL.legIndex,
    transportIds,
    carriers,
    departureIata: firstSegment.departureIata,
    departureDate: firstSegment.departureDateTime,
    arrivalIata: lastSegment.arrivalIata,
    arrivalDate: lastSegment.arrivalDateTime,
    durationInMinutes: leg.durationInMinutes,
    numberOfStops: leg.segments.length - 1,
    totalPrice: toMoney(alternativeSuggestionQL.totalPrice),
  };
}
